/**
 * Pure helpers for the Clever LMS admin dashboard. Colocated so unit
 * tests can exercise them without mounting the React tree.
 */

import type { CleverDistrictLmsState } from "../../services/clever-lms.service";

export type LmsStateAppearance = {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline" | "ghost";
  className: string;
};

/**
 * Map `lms_state` values to a badge appearance. Colours match the
 * ENGG-1886 spec: success=green, matching_in_progress=yellow,
 * disconnected=red, error=red. Unknown states render as neutral.
 */
export function describeLmsState(
  state: CleverDistrictLmsState | string | null | undefined,
): LmsStateAppearance {
  const normalized = (state ?? "").toString().toLowerCase();
  switch (normalized) {
    case "success":
      return {
        label: "Success",
        variant: "outline",
        className:
          "border-emerald-600/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
      };
    case "matching_in_progress":
      return {
        label: "Matching…",
        variant: "outline",
        className:
          "border-amber-600/30 bg-amber-500/15 text-amber-800 dark:text-amber-200",
      };
    case "disconnected":
      return {
        label: "Disconnected",
        variant: "outline",
        className:
          "border-red-600/30 bg-red-500/15 text-red-700 dark:text-red-300",
      };
    case "error":
      return {
        label: "Error",
        variant: "outline",
        className:
          "border-red-600/30 bg-red-500/15 text-red-700 dark:text-red-300",
      };
    default:
      return {
        label: normalized ? normalized.replace(/_/g, " ") : "Unknown",
        variant: "outline",
        className: "border-border/60 bg-muted/60 text-muted-foreground",
      };
  }
}

/**
 * Convert a raw 0..1 or 0..100 ratio into a display percentage.
 */
export function formatMatchedPercentage(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }
  const pct = value <= 1 ? value * 100 : value;
  if (!Number.isFinite(pct)) return "—";
  return `${pct.toFixed(1)}%`;
}

/**
 * Format an ISO timestamp to a compact local string, falling back to
 * the em-dash when missing or unparseable.
 */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleString();
}

/**
 * Human-friendly label for an unresolved roster reason code.
 */
export function describeRosterReason(reason: string | null | undefined): string {
  const normalized = (reason ?? "").toString().toLowerCase();
  switch (normalized) {
    case "no_evertutor_match":
      return "No EverTutor match";
    case "duplicate":
      return "Duplicate";
    case "incomplete":
      return "Incomplete";
    case "":
      return "Unknown";
    default:
      return normalized.replace(/_/g, " ");
  }
}

/**
 * Truncate an error string for a single-line table cell, preserving the
 * head so it's still useful.
 */
export function truncateError(
  value: string | null | undefined,
  maxLength = 80,
): string {
  if (!value) return "—";
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1)}…`;
}

/**
 * Render any JSON-ish value for the gradeback payload viewer. We stringify
 * objects with 2-space indent; primitives are coerced to string.
 */
export function formatPayload(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
